import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';

import { HIT_SQUARE, HIT_Y } from '@/components/ui/hit-area';
import { cn } from '@/lib/utils';

/**
 * Restylé aux specs Arti Pilotage (design/components/core/Button.jsx) :
 * Hanken 700, hover = teinte plus foncée + lift 1px, press = scale .96 (120ms),
 * en sombre le plein pose l'encre sur orange vif (jamais blanc — 3,3:1).
 * L'API shadcn (variants/tailles) est conservée pour les composants composés.
 */

const buttonVariants = cva(
  "group/button relative inline-flex shrink-0 cursor-pointer items-center justify-center border border-transparent bg-clip-padding font-sans font-bold whitespace-nowrap transition-[background-color,box-shadow,transform,color] duration-160 ease-standard outline-none select-none hover:not-disabled:-translate-y-px active:not-aria-[haspopup]:translate-y-0 active:not-aria-[haspopup]:scale-[0.96] active:duration-120 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:bg-disabled disabled:text-disabled-fg disabled:shadow-none aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-brand hover:not-disabled:bg-action-hover hover:not-disabled:shadow-brand-hover',
        secondary:
          'bg-action-wash text-action-on-surface shadow-[inset_0_0_0_1.5px_var(--action-line)] hover:not-disabled:bg-action-wash-hover',
        ghost:
          'text-primary hover:not-disabled:bg-primary/8 aria-expanded:bg-primary/8 dark:text-foreground dark:hover:not-disabled:bg-foreground/8 dark:aria-expanded:bg-foreground/8',
        outline:
          'border-border bg-transparent text-foreground hover:not-disabled:bg-muted aria-expanded:bg-muted dark:border-input dark:hover:not-disabled:bg-input/50',
        destructive:
          'bg-danger-bg text-danger hover:not-disabled:bg-danger-bg-hover focus-visible:border-danger/40 focus-visible:ring-danger/20',
        link: 'text-primary underline-offset-4 hover:translate-y-0 hover:not-disabled:underline',
      },
      // Hauteur EXPLICITE, jamais déduite du padding (UI-07) : sinon la boîte
      // dépend de l'interlignage, et changer de pas de police déplace tous les
      // boutons — le passage à `text-body` a fait grandir le primaire de 45 à
      // 47 px avant que cette hauteur soit posée.
      size: {
        default: `h-10 gap-2 rounded-button px-[18px] text-body has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5 [&_svg:not([class*='size-'])]:size-4 ${HIT_Y}`,
        sm: `h-8 gap-1.5 rounded-field px-3.5 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-4 ${HIT_Y}`,
        lg: "h-12 gap-[9px] rounded-button px-[22px] text-body has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4 [&_svg:not([class*='size-'])]:size-6",
        icon: `size-10 rounded-button ${HIT_SQUARE}`,
        'icon-sm': `size-8 rounded-field ${HIT_SQUARE}`,
        'icon-lg': 'size-12 rounded-button',
        // Exception actée (UI-07) : `xs` et `icon-xs` sont les ornements DANS
        // un champ — chips de combobox. Hors de l'échelle par nature, et hors
        // zone tactile étendue, qui y volerait les clics de la saisie.
        xs: "h-6 gap-1 rounded-field px-2 text-sm has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        'icon-xs': "size-6 rounded-field [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  loading = false,
  onClick,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & { loading?: boolean }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      aria-busy={loading || undefined}
      // En chargement : curseur wait, opacité 75 %, clic neutralisé (spec DS).
      // preventDefault et pas `disabled` : le bouton reste focusable et annoncé
      // busy. Retirer onClick ne suffit pas — sur un type="submit" la soumission
      // est l'action par défaut du clic, pas le handler React.
      onClick={loading ? (event) => event.preventDefault() : onClick}
      className={cn(
        buttonVariants({ variant, size, className }),
        loading && 'cursor-wait opacity-75 hover:not-disabled:translate-y-0',
      )}
      {...props}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="size-4 shrink-0 animate-spin rounded-pill border-2 border-current border-r-transparent opacity-60 animation-duration-[900ms]"
        />
      ) : null}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
